export interface CaseStudy {
  id: string;
  title: string;
  description: string;
  category: string;
  duration: string;
  info: {
    number: string;
    name: string;
  }[];
  role: string;
  client: string;
  icon: string; // Lucide React icon name
  gradient: {
    from: string;
    to: string;
  };
  stats: {
    label: string;
    value: string;
  }[];
  problemStatement: string;
  solutionApproach: string;
  challenges: string[];
  results: string[];
  lessonsLearned: string[];
  technologies: string[];
  businessImpact: string;
}

export const caseStudies: CaseStudy[] = [
  {
    id: 'cs1',
    title: 'Clinical Decision Support System',
    description:
      'A comprehensive AI-powered platform designed to enhance clinical decision-making through evidence-based recommendations, real-time safety alerts, and seamless EHR integration. The system addresses the critical need for point-of-care clinical guidance while maintaining the highest standards of patient safety and regulatory compliance.',
    category: 'Healthcare AI',
    duration: '12 months',
    info: [
      { number: '95%+', name: 'Error Reduction' },
      { number: '60%', name: 'Faster Decisions' },
      { number: '99.9%', name: 'Uptime' },
    ],
    role: 'Lead AI Engineer',
    client: 'Regional Health System',
    icon: 'Activity',
    gradient: {
      from: 'from-blue-500',
      to: 'to-cyan-600',
    },
    stats: [
      { label: 'Duration', value: '12 months' },
      { label: 'Role', value: 'Lead AI Engineer' },
      { label: 'Client', value: 'Regional Health System' },
    ],
    problemStatement:
      'Healthcare professionals face several critical challenges in clinical decision-making: Information overload as medical knowledge doubles every 73 days, making it impossible to stay current. 250,000+ deaths annually in the US due to medical errors, many preventable. Workflow disruption caused by existing decision support tools interrupting clinical workflows. Inconsistent care quality based on provider knowledge and experience. Time constraints with average of 16+ minutes per patient visit spent searching for clinical information.',
    solutionApproach:
      'Developed a comprehensive Clinical Decision Support System with AI-powered clinical recommendations using advanced RAG architecture, real-time safety monitoring for drug interactions and contraindications, seamless EHR integration with Epic/Cerner/AllScripts, quality assurance framework with continuous monitoring, and comprehensive role-based training program. The system uses OpenAI GPT-4 Turbo for clinical reasoning, semantic search with vector embeddings, and FHIR R4 compliance for standards-based integration.',
    challenges: [
      'Ensuring AI model accuracy and clinical safety in high-stakes medical decisions',
      'Achieving seamless EHR integration without disrupting existing clinical workflows',
      'Maintaining HIPAA compliance and data security across all system components',
      'Balancing real-time performance requirements with comprehensive clinical analysis',
      'Managing regulatory compliance and FDA approval processes for medical AI',
    ],
    results: [
      '95%+ reduction in medication errors through real-time interaction checking',
      '60% faster clinical decision-making with evidence-based recommendations',
      '99.9% system uptime with seamless EHR workflow integration',
      '90%+ user adoption rate among clinical staff with 4.0/5.0 satisfaction score',
      '40% reduction in medical errors and 10-15% decrease in average length of stay',
    ],
    lessonsLearned: [
      'Clinical validation and physician oversight are critical for AI medical systems',
      'EHR integration requires deep understanding of healthcare workflows and standards',
      'Explainable AI and confidence scoring are essential for clinical trust and adoption',
      'Comprehensive training programs are crucial for successful healthcare AI deployment',
      'Continuous quality monitoring and outcome tracking drive system improvement',
    ],
    technologies: [
      'OpenAI GPT-4',
      'Python',
      'PostgreSQL',
      'Pinecone',
      'Redis',
      'FHIR R4',
      'SMART on FHIR',
      'Docker',
      'Kubernetes',
      'FastAPI',
      'React',
      'spaCy',
      'PyTorch',
    ],
    businessImpact:
      'Improved patient outcomes for 200K+ patients with 200%+ ROI',
  },
  {
    id: 'cs2',
    title: 'ML Training Pipeline Platform',
    description:
      "A scalable, distributed machine learning training platform that enables organizations to train, optimize, and deploy ML models at scale using Kubernetes orchestration and Ray's distributed computing framework. The platform addresses critical gaps in enterprise ML infrastructure with automated hyperparameter optimization and seamless model deployment.",
    category: 'ML Infrastructure',
    duration: '6 months',
    info: [
      { number: '5x', name: 'Speedup' },
      { number: '80%+', name: 'Utilization' },
      { number: '99.9%', name: 'Uptime' },
    ],
    role: 'ML Infrastructure Lead',
    client: 'DataScale Enterprise',
    icon: 'Cpu',
    gradient: {
      from: 'from-teal-600',
      to: 'to-green-600',
    },
    stats: [
      { label: 'Duration', value: '6 months' },
      { label: 'Role', value: 'ML Infrastructure Lead' },
      { label: 'Client', value: 'DataScale Enterprise' },
    ],
    problemStatement:
      'Organizations faced critical gaps in enterprise ML infrastructure: Scale limitations with traditional single-machine training hitting memory/compute walls with datasets >100GB. Manual hyperparameter tuning wasting weeks on inefficient grid searches. Model deployment complexity with 60% of ML models never reaching production. Poor resource utilization with average GPU utilization only 30-40%. Lack of reproducibility with 70% of ML experiments cannot be reproduced due to poor tracking.',
    solutionApproach:
      'Built a comprehensive ML training platform with distributed training supporting PyTorch, TensorFlow, and XGBoost with automatic data sharding and model parallelism. Implemented Ray Tune integration with ASHA scheduler for intelligent hyperparameter optimization. Created model serving infrastructure with REST API deployment, auto-scaling, and A/B testing framework. Designed Kubernetes orchestration with Ray cluster auto-scaling and persistent storage for data/models.',
    challenges: [
      'Managing Kubernetes complexity while maintaining ease of use for ML engineers',
      'Implementing fault tolerance and checkpointing for long-running distributed training jobs',
      'Balancing resource utilization with cost optimization across multi-tenant environments',
      'Ensuring model reproducibility and experiment tracking across different frameworks',
      'Achieving sub-100ms serving latency while maintaining high throughput and availability',
    ],
    results: [
      '5x reduction in model training time through distributed computing across 1-100 workers',
      '80%+ resource utilization across compute clusters with intelligent auto-scaling',
      '99.9% system uptime with fault tolerance and automatic checkpoint restoration',
      '15-20% improvement in model accuracy through automated hyperparameter optimization',
      '24-hour time-to-production with seamless CI/CD integration and model serving',
    ],
    lessonsLearned: [
      "Ray's distributed computing framework significantly simplifies complex ML orchestration",
      'Automated hyperparameter optimization provides better results than manual tuning',
      'Kubernetes native deployment is essential for enterprise ML infrastructure',
      'Comprehensive monitoring and observability are crucial for distributed ML systems',
      'User experience design is critical for ML platform adoption across different skill levels',
    ],
    technologies: [
      'Ray',
      'Kubernetes',
      'PyTorch',
      'TensorFlow',
      'XGBoost',
      'Ray Tune',
      'Ray Serve',
      'FastAPI',
      'MLflow',
      'Prometheus',
      'Grafana',
      'Docker',
    ],
    businessImpact:
      'Accelerated ML development for 250+ researchers with 40% cost reduction',
  },
  {
    id: 'cs3',
    title: 'B2B Multi-Modal Logistics Platform',
    description:
      'A cloud-native, event-driven logistics platform designed to revolutionize B2B supply chain operations through multi-modal transportation optimization, real-time visibility, and predictive analytics. The Enterprise Logistics Command Center transforms global logistics operations for Fortune 500 companies.',
    category: 'Enterprise Software',
    duration: '12 months',
    info: [
      { number: '25%', name: 'Cost Reduction' },
      { number: '95%+', name: 'On-Time Delivery' },
      { number: '99.95%', name: 'Uptime' },
    ],
    role: 'Solutions Architect',
    client: 'Fortune 500 Manufacturer',
    icon: 'Barcode',
    gradient: {
      from: 'from-purple-500',
      to: 'to-indigo-600',
    },
    stats: [
      { label: 'Duration', value: '12 months' },
      { label: 'Role', value: 'Solutions Architect' },
      { label: 'Client', value: 'Fortune 500 Manufacturer' },
    ],
    problemStatement:
      'Fortune 500 manufacturers faced critical logistics challenges: Lack of real-time visibility across multi-modal transportation networks. Inefficient coordination between truck, rail, sea, and air carriers. Manual processes causing 40% slower shipment processing. Fragmented systems leading to poor carrier visibility and exception handling. High logistics costs with no unified optimization platform for $12 trillion global logistics market.',
    solutionApproach:
      'Architected a comprehensive Enterprise Logistics Command Center with event-driven microservices architecture using Apache Kafka for distributed event streaming. Implemented intelligent route optimization across 5+ transport modes with cost vs. time algorithms. Built distributed inventory management supporting 100+ locations with real-time visibility. Created predictive analytics engine for demand forecasting and automated rebalancing. Designed CQRS/Event Sourcing pattern for complete audit trail and scalability.',
    challenges: [
      'Integrating complex multi-modal transportation networks with real-time optimization',
      'Ensuring 99.95% uptime for mission-critical logistics operations across global deployments',
      'Managing event processing throughput of 1000+ events/second with sub-200ms API response',
      'Implementing compliance requirements for ITAR, GDPR, and SOC 2 Type II across all regions',
      'Achieving seamless integration with 20+ carrier APIs and ERP systems (SAP, Oracle)',
    ],
    results: [
      '25-40% reduction in transportation costs through multi-modal optimization algorithms',
      '95%+ on-time delivery rate with real-time tracking and exception management',
      '99.95% platform uptime with event-driven architecture and auto-scaling',
      '40% faster shipment processing time with automated workflows and carrier integration',
      'Complete supply chain visibility across 100+ global facilities with predictive analytics',
    ],
    lessonsLearned: [
      'Event-driven architecture is essential for real-time logistics operations at scale',
      'Multi-modal optimization requires sophisticated algorithms balancing cost, time, and compliance',
      'Carrier API reliability demands robust fallback mechanisms and intelligent caching',
      'CQRS/Event Sourcing provides complete audit trail crucial for logistics compliance',
      'User experience design is critical for adoption across different organizational roles',
    ],
    technologies: [
      'Node.js',
      'NestJS',
      'Apache Kafka',
      'PostgreSQL',
      'MongoDB',
      'Redis',
      'Docker',
      'Kubernetes',
      'AWS',
      'GraphQL',
      'WebSocket',
      'React',
    ],
    businessImpact:
      'Transformed logistics operations for $12T global market with $50M ARR target',
  },
  {
    id: 'cs4',
    title: 'GPU-Accelerated Platform for Energy & Utilities',
    description:
      'A next-generation infrastructure monitoring platform that leverages GPU-accelerated computer vision and 3D visualization to transform how energy utilities manage, inspect, and maintain critical assets. The SmartGrid AI Monitor provides real-time, autonomous monitoring with predictive maintenance capabilities.',
    category: 'Computer Vision & AI',
    duration: '12 months',
    info: [
      { number: '99.2%', name: 'Accuracy' },
      { number: '120+', name: 'FPS' },
      { number: '84%', name: 'Cost Reduction' },
    ],
    role: 'Computer Vision Lead',
    client: 'Regional Power Authority',
    icon: 'HousePlug',
    gradient: {
      from: 'from-yellow-600',
      to: 'to-orange-700',
    },
    stats: [
      { label: 'Duration', value: '12 months' },
      { label: 'Role', value: 'Computer Vision Lead' },
      { label: 'Client', value: 'Regional Power Authority' },
    ],
    problemStatement:
      'Energy utilities faced critical infrastructure monitoring challenges: Spending $120,000+ annually per site on manual inspections with human inspectors missing 2-5% of critical defects. 30% of infrastructure failures were preventable with early detection. Manual processes could not provide 24/7 continuous monitoring, and inspection data was fragmented and difficult to analyze. This resulted in $200M average annual cost of unplanned outages and worker safety risks during manual inspections.',
    solutionApproach:
      'Developed SmartGrid AI Monitor with GPU-accelerated computer vision processing 120+ FPS with <50ms latency. Implemented real-time defect detection with 99.2% accuracy across 20+ defect types. Built immersive 3D asset visualization with thermal overlays and interactive navigation. Created predictive maintenance algorithms providing 48+ hour failure predictions. Designed multi-modal sensor integration combining visual, thermal, and LiDAR data for comprehensive asset monitoring.',
    challenges: [
      'Achieving 99.2% detection accuracy while processing 120+ FPS in real-time',
      'Implementing 3D visualization with 60 FPS rendering and 10M+ polygon support',
      'Integrating multi-modal sensor data (visual, thermal, LiDAR) with temporal synchronization',
      'Ensuring 99.9% system uptime for mission-critical infrastructure monitoring',
      'Scaling distributed processing across 100+ GPU nodes for enterprise deployment',
    ],
    results: [
      '99.2% defect detection accuracy with 120+ FPS processing and <50ms latency',
      '84% reduction in inspection costs compared to manual processes ($2.08M annual savings)',
      '60 FPS sustained 3D rendering with interactive asset visualization and thermal overlays',
      '48+ hour failure prediction accuracy with 85%+ reliability for preventive maintenance',
      '99.9% system uptime supporting 100+ concurrent camera feeds across distributed infrastructure',
    ],
    lessonsLearned: [
      'GPU acceleration is essential for real-time computer vision at enterprise scale',
      '3D visualization significantly improves defect understanding and maintenance planning',
      'Multi-modal sensor fusion provides more accurate detection than single-sensor approaches',
      'Predictive maintenance algorithms require extensive historical data and continuous learning',
      'Edge processing capabilities are crucial for critical infrastructure applications',
    ],
    technologies: [
      'PyTorch',
      'OpenCV',
      'CUDA',
      'TensorRT',
      'Three.js',
      'React',
      'FastAPI',
      'PostgreSQL',
      'InfluxDB',
      'Redis',
      'Docker',
      'Kubernetes',
    ],
    businessImpact:
      'Transformed infrastructure monitoring for $12T energy market with $50M ARR target',
  },
  {
    id: 'cs5',
    title: 'Autonomous Vehicle Computer Vision System',
    description:
      'A production-ready computer vision system for autonomous vehicles that performs real-time multi-task perception using distributed machine learning infrastructure. The system simultaneously performs object detection, semantic segmentation, depth estimation, and lane detection with industry-leading accuracy and speed.',
    category: 'Computer Vision & AI',
    duration: '6 months',
    info: [
      { number: '30+', name: 'FPS' },
      { number: '75%', name: 'mAP' },
      { number: '99.9%', name: 'Uptime' },
    ],
    role: 'Computer Vision Lead',
    client: 'Autonomous Vehicles',
    icon: 'CarFront',
    gradient: {
      from: 'from-cyan-600',
      to: 'to-emerald-600',
    },
    stats: [
      { label: 'Duration', value: '6 months' },
      { label: 'Role', value: 'Computer Vision Lead' },
      { label: 'Client', value: 'Autonomous Vehicle Manufacturer' },
    ],
    problemStatement:
      'Autonomous vehicle development faced critical perception challenges: Computational inefficiency requiring 4x resources to run separate models for each perception task. Sequential processing creating 100ms+ total latency exceeding real-time requirements. Training bottlenecks with single-GPU training taking 400+ hours for production-quality models. Integration complexity requiring multiple models with complex orchestration and synchronization for Level 4/5 autonomy.',
    solutionApproach:
      'Developed a multi-task learning system with single backbone network and task-specific heads processing object detection, semantic segmentation, depth estimation, and lane detection simultaneously. Implemented distributed training infrastructure scaling to 8+ GPUs with 75%+ efficiency. Built real-time inference pipeline achieving >30 FPS with <20ms latency. Created comprehensive MLOps pipeline with MLflow integration, automated hyperparameter tuning, and continuous learning from fleet data.',
    challenges: [
      'Achieving >30 FPS real-time inference while maintaining >75% mAP for object detection',
      'Implementing multi-task learning with single backbone for computational efficiency',
      'Ensuring <20ms latency for safety-critical decision making in autonomous vehicles',
      'Scaling distributed training to 8+ GPUs while maintaining 75%+ efficiency',
      'Meeting ISO 26262 ASIL-B compliance requirements for perception systems',
    ],
    results: [
      '>30 FPS real-time inference with <20ms latency on edge hardware',
      '75%+ mAP for object detection and 70%+ mIoU for semantic segmentation',
      'Linear scaling up to 8 GPUs for training with 6x speedup over single-GPU',
      '99.9% uptime in production deployment with automatic failover capabilities',
      'Support for 6+ cameras with 360° coverage and temporal consistency tracking',
    ],
    lessonsLearned: [
      'Multi-task learning significantly reduces computational requirements compared to separate models',
      'Real-time inference requires careful optimization of model architecture and deployment',
      'Distributed training infrastructure is essential for production-quality model development',
      'Safety-critical applications demand rigorous testing and compliance validation',
      'Edge deployment optimization is crucial for autonomous vehicle constraints',
    ],
    technologies: [
      'PyTorch',
      'Ray',
      'Kubernetes',
      'MLflow',
      'Ray Serve',
      'NVIDIA Triton',
      'OpenCV',
      'CUDA',
      'TensorRT',
      'Prometheus',
      'Grafana',
      'MinIO',
    ],
    businessImpact:
      'Enabled Level 4/5 autonomy for $556B autonomous vehicle market',
  },
  {
    id: 'cs6',
    title: 'AI Finance Intelligence System',
    description:
      'An automated financial technology platform that leverages real-time market data, artificial intelligence, and algorithmic optimization to provide institutional-grade portfolio management capabilities. The system continuously monitors market conditions, optimizes asset allocation, and executes rebalancing strategies while maintaining comprehensive risk management protocols.',
    category: 'Financial Technology',
    duration: '6 months',
    info: [
      { number: '99.9%', name: 'Uptime' },
      { number: '90%', name: 'Cost Reduction' },
      { number: '1000+', name: 'Securities' },
    ],
    role: 'Solutions Architect',
    client: 'Financial Technology',
    icon: 'TrendingUp',
    gradient: {
      from: 'from-rose-700',
      to: 'to-fuchsia-700',
    },
    stats: [
      { label: 'Duration', value: '6 months' },
      { label: 'Role', value: 'Solutions Architect' },
      { label: 'Client', value: 'Financial Technology' },
    ],
    problemStatement:
      'Individual investors and financial advisors faced critical challenges in portfolio management: lack of access to real-time optimization tools, manual rebalancing prone to emotional bias, cost-prohibitive institutional-grade analytics, and complex integration of multiple data sources. The market needed a democratized solution that could provide professional-grade investment optimization previously available only to institutional investors.',
    solutionApproach:
      'Architected a comprehensive AI-powered platform featuring real-time data aggregation from multiple sources (Polygon.io, Alpha Vantage, Yahoo Finance), Modern Portfolio Theory optimization algorithms, AI-powered market analysis using GPT-4, comprehensive risk management with VaR/CVaR calculations, and automated execution engine with smart order routing. Implemented n8n workflow orchestration for seamless automation and PostgreSQL with Redis for scalable data persistence.',
    challenges: [
      'Real-time data processing with <2 second latency for 1000+ securities',
      'Portfolio optimization algorithms handling complex constraints and transaction costs',
      'AI integration for natural language market analysis with confidence scoring',
      'Risk management system with multiple metrics and real-time alerting',
      'Automated trade execution with circuit breakers and smart order routing',
      'Scalability to handle 1000+ concurrent portfolio analyses',
    ],
    results: [
      'Portfolio Sharpe Ratio > 1.5 (target achieved)',
      'System uptime 99.9% (exceeded target)',
      'Data processing latency < 5 seconds (target: <5s)',
      'Portfolio returns beat benchmark by 2-5% (quarterly)',
      'Cost reduction 90% vs traditional portfolio management tools',
      'Support for 1000+ securities with real-time processing',
      'AI-powered analysis with confidence scoring and human-readable reports',
    ],
    lessonsLearned: [
      'Real-time data processing and low-latency systems are critical for financial applications',
      'Risk management is paramount in automated trading systems - multiple fallbacks essential',
      'AI integration significantly enhances market sentiment analysis and decision-making',
      'Comprehensive testing and regulatory compliance are non-negotiable in fintech',
      'Workflow orchestration (n8n) provides excellent flexibility for complex financial processes',
      'Democratizing institutional-grade tools requires careful UX design and gradual feature rollout',
    ],
    technologies: [
      'n8n',
      'PostgreSQL',
      'Redis',
      'OpenAI GPT-4',
      'React',
      'Next.js',
      'Docker',
      'Kubernetes',
      'Polygon.io',
      'Alpha Vantage',
      'Grafana',
      'Prometheus',
    ],
    businessImpact:
      'Democratized access to institutional-grade portfolio management tools, targeting $50M ARR by Year 3 with 8% market penetration of North American retail investment market and 100+ enterprise clients by Year 2.',
  },
];
